import React, { useMemo } from "react";
import {
    Icon, Form, Button, Schema, Input, Checkbox,
    SelectPicker, FormControl, Whisper, Tooltip,
    CheckPicker, DatePicker
} from "rsuite";
import styled, { css } from "styled-components";

import { weekDaysData } from "const";
import TableEditCellsNew from "components/Table/TableEditCells/TableEditCellsNew";

const { NumberType } = Schema.Types;


const parseTimeToDate = (timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
        return null;
    }

    const [hours = "0", minutes = "0"] = timeValue.split(":");

    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);

    return date;
};

const formatDateToTime = (dateValue) => {
    if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
        return "";
    }

    const pad = (value) => String(value).padStart(2, "0");

    return `${pad(dateValue.getHours())}:${pad(dateValue.getMinutes())}`;
};

const formatTimePreview = (timeValue) => {
    if (!timeValue) {
        return "";
    }

    if (timeValue instanceof Date) {
        return formatDateToTime(timeValue);
    }

    if (typeof timeValue === "string") {
        const [hours = "00", minutes = "00"] = timeValue.split(":");

        return `${hours}:${minutes}`;
    }

    return "";
};

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const buildTimeRange = (fromTime, toTime) => {
    const fromDate = isValidDate(fromTime) ? fromTime : parseTimeToDate(fromTime);
    const toDate = isValidDate(toTime) ? toTime : parseTimeToDate(toTime);

    if (!isValidDate(fromDate) || !isValidDate(toDate)) {
        return null;
    }

    return [fromDate, toDate];
};

const formatTimeRangePreview = (fromTime, toTime) => {
    const fromValue = formatTimePreview(fromTime);
    const toValue = formatTimePreview(toTime);

    if (!fromValue && !toValue) {
        return "";
    }

    if (fromValue === "00:00" && toValue === "23:59") {
        return "All day";
    }

    return `${fromValue} - ${toValue}`;
};

const toTimeInputValue = (value) => {
    if (value instanceof Date) {
        return formatDateToTime(value);
    }

    if (typeof value === "string") {
        const [hours = "00", minutes = "00"] = value.split(":");
        return `${hours}:${minutes}`;
    }

    return "";
};

const toTimePickerValue = (value) => {
    if (isValidDate(value)) {
        return value;
    }

    return parseTimeToDate(value);
};

const toDateWithTime = (baseDate, timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
        return null;
    }

    const [hours = "0", minutes = "0"] = timeValue.split(":");
    const nextDate = isValidDate(baseDate) ? new Date(baseDate) : new Date();
    nextDate.setHours(Number(hours), Number(minutes), 0, 0);

    return nextDate;
};

const TimeRangeAccepter = React.forwardRef(({ value, onChange, disabled }, ref) => {
    const range = Array.isArray(value) ? value : [null, null];
    const [fromValue, toValue] = range;

    return (
        <TimeRangeWrapper ref={ref}>
            <TimePickerInput
                format="HH:mm"
                showMeridian={false}
                ranges={[]}
                cleanable={false}
                placeholder="00:00"
                placement="auto"
                value={toTimePickerValue(fromValue)}
                disabled={disabled}
                onChange={(nextFrom) => {
                    const nextTo = isValidDate(toValue) ? toValue : toDateWithTime(nextFrom, toTimeInputValue(toValue));
                    onChange([nextFrom, nextTo]);
                }}
            />
            <TimePickerInput
                format="HH:mm"
                showMeridian={false}
                ranges={[]}
                cleanable={false}
                placeholder="23:59"
                placement="auto"
                value={toTimePickerValue(toValue)}
                disabled={disabled}
                onChange={(nextTo) => {
                    const nextFrom = isValidDate(fromValue) ? fromValue : toDateWithTime(nextTo, toTimeInputValue(fromValue));
                    onChange([nextFrom, nextTo]);
                }}
            />
        </TimeRangeWrapper>
    );
});

TimeRangeAccepter.displayName = "TimeRangeAccepter";

const getCheckedWeekDayIds = (weekDays = []) => {
    if (weekDays.every(day => typeof day === "number")) {
        return weekDays;
    }

    return weekDays
        .filter(day => day && day.checked)
        .map(day => day.id);
};

const formatWeekDaysPreview = (weekDays = []) => {
    const checkedIds = getCheckedWeekDayIds(weekDays);

    if (checkedIds.length === weekDaysData.length) {
        return "All week";
    }

    return weekDaysData
        .filter(day => checkedIds.includes(day.value))
        .map(day => day.shortLabel)
        .join(", ");
};

const hasFiltering = ({ phrases, patterns }) => (
    (Array.isArray(phrases) && phrases.length > 0)
    || (Array.isArray(patterns) && patterns.length > 0)
);


const FakeDLRTable = ({
    data,
    service,
    loading,
    editData,
    removeKeys,
    isMobile,

    operatorList,
    countryList,

    renderSuppliersItem,
    renderSuppliersValue,

    setEditData,
    setRemoveKeys,
    getSupplierPerTrunkList,

    onRowSubmit,
    onRowEdit,
    onDataUpdate,

    ...props
}) => {
    const rowIdKey = "sms.fake_dlr_id";

    const fakeDLRModel = Schema.Model({
        percent: NumberType()
            .min(0, "Must be non-negative")
            .max(100, "Maximum 100")
    });

    const callsHeader = <span>
        Calls <Whisper
            placement="bottomEnd"
            trigger="hover"
            speaker={<Tooltip>Minimum calls per number</Tooltip>}
        >
            <Icon icon="question-circle" />
        </Whisper>
    </span>;

    const supplierList = getSupplierPerTrunkList();
    const createSupplierList = getSupplierPerTrunkList(true);

    const columns = useMemo(() => {
        return [
            {
                id: "supplier_id",
                name: "Supplier",
                dataKey: "id",
                dataType: "nullable",
                value: ({ supplier_name, supplier_trunk_name }) => {
                    const displayValue = `${supplier_name} / ${supplier_trunk_name}`;
                    return <Whisper trigger="hover" speaker={<Tooltip>{displayValue}</Tooltip>}>
                        <span>{displayValue}</span>
                    </Whisper>;
                },
                defaultValue: ({ id }) => {
                    return id;
                },
                align: "left",
                className: "supplier_id",
                placeholder: "Supplier...",
                // width: 260,
                flexGrow: 2,
                minWidth: 220,

                loading: loading,
                disabledOnEdit: editData.mode === "multiple",
                accepter: SelectPicker,
                renderMenuItem: renderSuppliersItem,
                renderValue: renderSuppliersValue,
                disabledItemValues: supplierList.filter(supplier => !supplier.trunk_id).map(supplier => supplier.id),
                virtualized: false,
                customData: true,
                modifyData: supplierList || [],
                createData: createSupplierList || [],
                valueKey: "id",
                labelKey: "supplier_name"
            },
            {
                id: "senderid",
                name: "Sender ID",
                placeholder: "Sender ID...",
                dataKey: "senderid",
                dataType: "string",
                defaultValue: ({ senderid }) => {
                    return senderid || "";
                },
                value: ({ senderid }) => {
                    return `${senderid || ""}`;
                },
                width: 140,
                loading: loading,
                disabledOnEdit: editData.mode === "multiple",
                accepter: Input
            },
            {
                id: "country",
                name: "Country",
                dataKey: "mcc",
                dataType: "nullable",
                value: ({ mcc_name }) => {
                    return mcc_name || "";
                },
                defaultValue: ({ mcc }) => {
                    return mcc;
                },
                align: "left",
                className: "country",
                placeholder: "Country...",
                // width: 260,
                flexGrow: 1,
                // minWidth: 220,
                menuStyle: { width: 280 },

                loading: loading,
                disabledOnEdit: editData.mode === "multiple",
                accepter: SelectPicker,
                data: countryList || [],
                labelKey: "mcc_name",
                valueKey: "mcc"
            },
            {
                id: "operator",
                name: "Operator",
                dataKey: "mcc_mnc",
                dataType: "nullable",
                value: ({ mcc_mnc_name }) => {
                    return mcc_mnc_name || "";
                },
                defaultValue: ({ mcc_mnc }) => {
                    return mcc_mnc;
                },
                align: "left",
                className: "operator",
                placeholder: "Operator...",
                // width: 260,
                flexGrow: 1,
                minWidth: 220,
                menuStyle: { width: 280 },

                loading: loading,
                disabledOnEdit: editData.mode === "multiple",
                accepter: SelectPicker,
                data: operatorList || [],
                labelKey: "mcc_mnc_name",
                valueKey: "mcc_mnc"
            },
            {
                id: "percent",
                name: "Fake DLR percent, %",
                placeholder: "Percent...",
                align: "right",
                dataKey: "percent",
                dataType: "string",
                defaultValue: ({ percent }) => {
                    return percent || "";
                },
                value: ({ "sms.fake_dlr_id": key, first, percent }) => {
                    if (editData.mode === "multiple") {
                        const currentValue = editData.draft && editData.draft.find(item => item["sms.fake_dlr_id"] === key);
                        const valueHasErrors = editData.draft && !editData.draft.length;

                        return (<TableForm
                            model={fakeDLRModel}
                            formValue={currentValue}
                            formDefaultValue={{ percent: percent }}
                            onChange={(formValue) => {
                                // setData
                                setEditData((prev) => {
                                    return {
                                        ...prev,
                                        draft: updateRows(prev.draft, key, "percent", formValue["percent"])
                                    }
                                })
                            }}
                        >
                            <MultiPanel>
                                <FormControl
                                    name="percent"
                                    disabled={key === null}
                                />
                                {first && <Whisper
                                    speaker={<Tooltip>Apply day to all rows</Tooltip>}
                                    placement="bottomEnd"
                                >
                                    <StyledButton
                                        color="blue"
                                        disabled={valueHasErrors}
                                        onClick={() => {
                                            const value = currentValue && currentValue.percent ? currentValue.percent : percent;

                                            applyValueToAll("percent", value);
                                        }}
                                    >
                                        <Icon icon="arrow-down" />
                                    </StyledButton>
                                </Whisper>}
                            </MultiPanel>
                        </TableForm>)
                    }

                    return percent;
                },
                // flexGrow: 1,
                width: 130,
                loading: loading,
                accepter: Input,
                errorPlacement: "topStart"
            },
            {
                id: "time",
                name: "Period",
                dataKey: "from_time",
                align: "left",
                dataType: "array",
                value: ({ from_time, to_time }) => {
                    return formatTimeRangePreview(from_time, to_time);
                },
                formValue: (rowData, currentValue) => {
                    if (Array.isArray(currentValue) && currentValue.length === 2) {
                        return currentValue;
                    }

                    return buildTimeRange(rowData.from_time, rowData.to_time);
                },
                defaultValue: ({ from_time, to_time }) => {
                    return buildTimeRange(from_time, to_time);
                },
                width: 110,
                loading: loading,
                accepter: TimeRangeAccepter,
                errorPlacement: "topStart",
                placeholder: "Select time range"
            },
            {
                id: "days_of_week",
                name: "Days of week",
                dataKey: "week_days",
                align: "left",
                dataType: "array",
                value: ({ week_days }) => {
                    return formatWeekDaysPreview(week_days);
                },
                formValue: (_, currentValue) => {
                    return getCheckedWeekDayIds(currentValue);
                },
                defaultValue: ({ week_days }) => {
                    return getCheckedWeekDayIds(week_days);
                },
                // flexGrow: 1,
                width: 190,
                loading: loading,
                accepter: CheckPicker,
                data: weekDaysData,
                valueKey: "value",
                labelKey: "label",
                cleanable: false,
                searchable: false,
                errorPlacement: "topStart",
                placeholder: "Days..."
            },
            {
                id: "min_calls_per_hour",
                name: callsHeader,
                align: "right",
                dataKey: "min_calls_per_hour",
                dataType: "string",
                defaultValue: ({ min_calls_per_hour }) => {
                    return min_calls_per_hour || "";
                },
                // flexGrow: 1,
                width: 80,
                loading: loading,
                accepter: Input,
                errorPlacement: "topStart",
                placeholder: "Calls"
            },
            {
                id: "filtering",
                name: "",
                dataKey: "filtering",
                align: "center",
                width: 44,
                value: (rowData) => hasFiltering(rowData) ? (
                    <Whisper
                        placement="bottomEnd"
                        trigger="hover"
                        speaker={<Tooltip>This rule has filtering.</Tooltip>}
                    >
                        <FilteringIcon icon="certificate" />
                    </Whisper>
                ) : null,
                loading: loading,
                disabledOnEdit: true
            },
            ...(editData.mode === "multiple" ? [
                {
                    id: "select",
                    name: "Select to delete",
                    dataKey: "select",
                    align: "center",
                    width: 120,
                    value: ({ "sms.fake_dlr_id": key, first }) => {
                        const currentValue = editData.draft && editData.draft.find(item => item.key === key);
                        const valueHasErrors = editData.draft && !editData.draft.length;

                        return (
                            <MultiPanel noForm>
                                <StyledCheckbox
                                    checked={removeKeys.includes(key)}
                                    onChange={(_, checked) => {
                                        if (!removeKeys.includes(key) && checked) {
                                            setRemoveKeys(prev => [...prev, key]);
                                        } else if (removeKeys.includes(key) && !checked) {
                                            setRemoveKeys(prev => prev.filter(k => k !== key));
                                        }
                                    }}
                                />
                                {first && <Whisper
                                    speaker={<Tooltip>Apply select to all rows</Tooltip>}
                                    placement="bottomEnd"
                                >
                                    <StyledButton
                                        color="blue"
                                        disabled={valueHasErrors}
                                        onClick={() => {
                                            applyValueToAll("remove", removeKeys.includes(key));
                                        }}
                                    >
                                        <Icon icon="arrow-down" />
                                    </StyledButton>
                                </Whisper>}
                            </MultiPanel>
                        )
                    }
                }
            ] : [])
        ];
    }, [loading, editData.mode, editData.draft, operatorList, supplierList, createSupplierList, removeKeys]);


    const applyValueToAll = (key, value) => {
        const filterDefaults = (list) => list.filter(item => item["sms.fake_dlr_id"] !== null);

        let newDraft = [];

        if (key === "remove") {
            if (value) {
                setRemoveKeys(filterDefaults(editData.draft).map(item => item["sms.fake_dlr_id"]));
            } else {
                setRemoveKeys([]);
            }
            return;
        }

        setEditData(prev => {
            newDraft = filterDefaults(prev.draft).map(item => ({
                ...item,
                [key]: value,
                edited: true
            }));
            return { ...prev, draft: newDraft };
        });
    };

    const updateRows = (rows, key, dataKey, value) =>
        rows.map(row =>
            row["sms.fake_dlr_id"] === key
                ? { ...row, edited: true, [dataKey]: value }
                : row
        );

    return (
        <>
            <StyledTable
                rowIdKey={rowIdKey}
                defaultRowIdKey={rowIdKey}
                rowClassName={(rowData) => {
                    if (rowData)
                        return rowData.undeletable ? "undeletable-row" : ""
                }}
                virtualized
                loading={loading}
                data={data}
                height={592}
                columns={columns}
                headerHeight={40}
                autoHeight={false}
                shouldUpdateScroll={false}
                showFormCreate={false}
                addButtonWidth={editData.mode !== "multiple" ? 80 : 1}
                modalRemoveTitle="Remove Fake DLR"
                // canCreate={!!service && !isMobile}
                canModify={editData.mode !== "multiple"}
                canRemove={editData.mode !== "multiple"}
                onRowEdit={editData.mode !== "multiple" ? onRowEdit : null}
                onRowSubmit={onRowSubmit}
                onDataUpdate={onDataUpdate}

                pagination
                {...props}
            />
        </>
    )
};

export default FakeDLRTable;

const TimeRangeWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 2px;
`;

const TimePickerInput = styled(DatePicker)`
    && {
        width: 90px;
        max-width: 90px;
    }

    && .rs-picker-toggle,
    && .rs-picker-input-group {
        border-color: gray !important;
        border-width: 2px !important;
        border-style: solid !important;
    }

    && .rs-picker-toggle.active,
    && .rs-picker-input-group.rs-input-group-focus {
        border-width: 1px !important;
    }

    && .rs-picker-toggle:hover,
    && .rs-picker-input-group:hover {
        border-color: lightgray !important;
    }

    && .rs-input {
        padding-left: 8px;
        padding-right: 8px;
        text-align: center;
    }
`;


const StyledTable = styled(TableEditCellsNew)`
    &&.rs-table.rs-table-hover {
        margin-top: 0;
        margin-bottom: 0;
    }

    .rs-table-row.undeletable-row .rs-table-cell-content {
        background: lightgray;
    }

    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons {
        float: right;
    }

    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons .rs-btn:last-child {
        margin-right: 0px !important;
    }

    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons > .rs-btn {
        width: 28px;
        height: 28px;
        padding: 4px 4px;
    }

    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons > .rs-btn {
        margin-right: 5px;
    }
    .rs-table-body-row-wrapper .rs-table-cell-content .tableFilters_buttons .rs-btn-lightblue {
        color: var(--color-light);
        background: var(--color-info);
    }

    .rs-error-message.rs-error-message-show {
        position: absolute;
        color: red;
        top: -4px;
        font-size: 10px;
        border-radius: 10px;
        bottom: auto !important;
        padding: 0 4px;
        right: 6px !important;
    }

    .rs-error-message.rs-error-message-show .rs-error-message-arrow {
        display: none;
    }
    .rs-error-message.rs-error-message-show .rs-error-message-inner {
        white-space: nowrap;
    }
`;

const TableForm = styled(Form)`
    && {
        margin-top: -7px;
    }

    && .rs-input {
        overflow: visible !important;
    }

    && .rs-error-message.rs-error-message-show {
        margin-top: -12px;
        border-radius: 5px;
        padding: 0px 6px;
        line-height: 14px;
        right: inherit !important;
        left: 0;
    }
`;

const StyledButton = styled(Button)`
    && {
        /* margin-top: -4px; */
        margin-left: 5px;
        min-width: 22px;
        height: 36px;
        padding: 0px;
    }
`;

const MultiPanel = styled.div`
    && {
        display: flex;
        flex-direction: row;
        justify-content: end;
        align-items: center;
        margin-top: -4px;
    }

    ${props => props.noForm && css`
        && {
            margin-top: -7px;
        }
    `}
`;

const StyledCheckbox = styled(Checkbox)`
    && {
        margin-left: 42px;
        /* margin-top: -4px; */
        width: 36px;
    }
`;

const FilteringIcon = styled(Icon)`
    color: var(--color-error2, #d9534f);
`;

